import { isValid } from "cpf";
import { IWrapperValidate } from "#components/primitive";
import { validate } from "uuid";

import { StepperErros, StepperTouched, StepperWarnings } from "#components/stepper";
import { get } from "lodash-es";
import { phoneWithDDDMask } from "./use-masks";

// eslint-disable-next-line max-len
export const cpfRegex = /\d{3}[\.]?\d{3}[\.]?\d{3}[-]?\d{2}/;
export const cnpjRegex = /\d{2}[\.]?\d{3}[\.]?\d{3}[\/]?\d{4}[-]?\d{2}/;
export const emailRegex = /\S+@\S+\.\S+/;
export const phoneRegex = /\+? ?[1-9]{2}[\(]?[1-9]{2}[\)]? ?9\d{4}[\-]?\d{4}/;

export const bankAccountAgencyRegex = /\d{4}/;

export type Error<T> = {
	[field in keyof T]?: any;
};

export type Touched<T> = {
	[field in keyof T]?: any;
};

export type ValidateStepperField<T> = (
	errors: StepperErros<T>,
	warnings: StepperWarnings<T>,
	touched: StepperTouched<T>,
	field: keyof T,
) => IWrapperValidate | undefined;

export function useValidations<Values>() {
	function validateFormikField(
		errors: Error<Values>,
		touched: Touched<Values>,
		field: GlobalTypes.ExtractKeysWithNullable<Values>,
	): IWrapperValidate | undefined {
		const error = get(errors, field);
		const isTouched = !!get(touched, field);

		if (field.includes("recurringPayment")) {
			console.log(field, { errors, touched, error, isTouched, field });
		}

		if (error && isTouched && typeof error === "string") {
			return { type: "error", value: error };
		}
	}

	function validateStepperField(
		errors: StepperErros<Values>,
		warnings: StepperWarnings<Values>,
		touched: StepperTouched<Values>,
		field: GlobalTypes.ExtractKeys<Values>,
	): IWrapperValidate | undefined {
		const error = get(errors, field);
		const isTouched = !!get(touched, field);
		const warning = get(warnings, field);

		if (!!error && isTouched && typeof error === "string") {
			return { type: "error", value: error };
		}

		if (!!warning && isTouched && typeof warning === "string") {
			return { type: "warning", value: warning };
		}
	}

	function validateCpf(cpf: string) {
		cpf = cpf.replace(/\D+/g, "");
		return isValid(cpf) && cpfRegex.test(cpf);
	}

	function validateCnpj(cnpj: string) {
		// Remove period, slash and dash characters from CNPJ
		const cleaned = cnpj.toString().replace(/[./-]/g, "");

		const CNPJ_LENGTH = 14;
		const INDEX_ZERO = 0;
		const CNPJ_DIGITS_LENGTH = 2;
		const MAX_MULTIPLICATION_ALGARISM = 9;
		const MIN_MULTIPLICATION_ALGARISM = 2;
		const INCREMENT = 1;
		const MAX_MOD = 2;
		const VERIFICATION_DIGIT = 0;
		const CNPJ_SUM_DIVIDER = 11;
		const INITIAL_MULTIPLICATION_INDEX = 2;

		function digit(numbers: string) {
			let currentIndex = INITIAL_MULTIPLICATION_INDEX;
			const sum = numbers
				.split("")
				.reverse()
				.reduce((buffer, number) => {
					let newBuffer = buffer;
					newBuffer += Number(number) * currentIndex;
					currentIndex =
						currentIndex === MAX_MULTIPLICATION_ALGARISM
							? MIN_MULTIPLICATION_ALGARISM
							: currentIndex + INCREMENT;
					return newBuffer;
				}, INDEX_ZERO);
			const mod = sum % CNPJ_SUM_DIVIDER;
			return mod < MAX_MOD ? VERIFICATION_DIGIT : CNPJ_SUM_DIVIDER - mod;
		}

		if (
			cleaned.length !== CNPJ_LENGTH ||
			// Must be digits and not be sequential characters (e.g.: 11111111111111, etc)
			/^(\d)\1+$/.test(cleaned)
		) {
			return false;
		}

		let registration = cleaned.slice(INDEX_ZERO, CNPJ_LENGTH - CNPJ_DIGITS_LENGTH);
		// find the first verification digit
		registration += digit(registration);
		// find the second verification digit
		registration += digit(registration);
		// check if the found verification digits matches the inputed
		return registration.slice(-CNPJ_DIGITS_LENGTH) === cleaned.slice(-CNPJ_DIGITS_LENGTH);
	}

	function validateEmail(email: string) {
		return emailRegex.test(email);
	}

	function validatePhone(phone: string) {
		return phoneWithDDDMask.test(phone);
	}

	function validateUuid(uuid: string) {
		return validate(uuid);
	}
	function validateBankAccountAgency(agency: string) {
		return bankAccountAgencyRegex.test(agency);
	}

	return {
		validateFormikField,
		validateStepperField,
		validateCpf,
		validateCnpj,
		validateEmail,
		validatePhone,
		validateUuid,
		validateBankAccountAgency,
	};
}
